import { DatabaseEngine, InstanceType } from "../types/database.type";

export const databaseEngines: DatabaseEngine[] = [
  {
    id: 'postgresql',
    name: 'PostgreSQL',
    icon: '🐘',
    image: '/images/postgres.png',
    description: 'Open-source relational database',
    color: 'from-blue-500 to-blue-600',
    versions: ['16.1', '15.5', '14.10', '13.13'],
    defaultPort: 5432,
    category: 'relational',
  },
  {
    id: 'mysql',
    name: 'MySQL',
    icon: '🐬',
    image: '/images/mysql.png',
    description: 'Popular open-source database',
    color: 'from-orange-500 to-orange-600',
    versions: ['8.0.35', '8.0.33', '5.7.44'],
    defaultPort: 3306,
    category: 'relational',
  },
  {
    id: 'mariadb',
    name: 'MariaDB',
    icon: '🦭',
    description: 'MySQL-compatible database',
    color: 'from-teal-500 to-teal-600',
    versions: ['10.11', '10.6', '10.5'],
    defaultPort: 3306,
    category: 'relational',
  },
  {
    id: 'sqlserver',
    name: 'SQL Server',
    icon: '📊',
    image: '/images/sqlserver.png',
    description: 'Microsoft SQL Server',
    color: 'from-red-500 to-red-600',
    versions: ['2022', '2019', '2017'],
    defaultPort: 1433,
    category: 'relational',
  },
  {
    id: 'mongodb',
    name: 'MongoDB',
    icon: '🍃',
    image: '/images/mongodb.png',
    description: 'NoSQL document database',
    color: 'from-green-500 to-green-600',
    versions: ['8.0', '7.0', '6.0'],
    defaultPort: 27017,
    category: 'nosql',
  },
  {
    id: 'documentdb',
    name: 'DocumentDB',
    icon: '📄',
    description: 'MongoDB-compatible service',
    color: 'from-purple-500 to-purple-600',
    versions: ['5.0', '4.0'],
    defaultPort: 27017,
    category: 'nosql',
    available: false, // sin producto ni backend (DocDB es otro servicio de AWS)
  },
  {
    id: 'aurora-mysql',
    name: 'Aurora MySQL',
    icon: '⚡',
    description: 'MySQL-compatible Aurora',
    color: 'from-cyan-500 to-cyan-600',
    versions: ['8.0', '5.7'],
    defaultPort: 3306,
    category: 'managed',
    available: false, // necesita DBCluster, no DBInstance simple; sin producto
  },
  {
    id: 'aurora-postgresql',
    name: 'Aurora PostgreSQL',
    icon: '⚡',
    description: 'PostgreSQL-compatible Aurora',
    color: 'from-indigo-500 to-indigo-600',
    versions: ['15.4', '14.9', '13.12'],
    defaultPort: 5432,
    category: 'managed',
    available: false, // necesita DBCluster, no DBInstance simple; sin producto
  },
];

/**
 * Obtiene la imagen o icono del engine por nombre (PostgreSQL, MySQL, sqlserver-web,
 * "SQL Server", etc.). Chequeo por PRIORIDAD, sin espacios/guiones, para que
 * "SQL Server" o "sqlserver-web" no caigan por error en PostgreSQL (porque
 * "postgresql" contiene "sql"). sqlserver/mariadb antes que las genéricas.
 */
export const getEngineDisplay = (engineName: string): { image?: string; icon: string } => {
  const key = (engineName || '').toLowerCase().replace(/[\s_-]/g, '');
  if (key.includes('sqlserver') || key.includes('mssql')) {
    return { image: '/images/sqlserver.png', icon: '📊' };
  }
  if (key.includes('mariadb')) {
    return { image: undefined, icon: '🦭' };
  }
  if (key.includes('mysql')) {
    return { image: '/images/mysql.png', icon: '🐬' };
  }
  if (key.includes('mongo')) {
    return { image: '/images/mongodb.png', icon: '🍃' };
  }
  if (key.includes('documentdb')) {
    return { image: undefined, icon: '📄' };
  }
  if (key.includes('postgres')) {
    return { image: '/images/postgres.png', icon: '🐘' };
  }
  return { image: undefined, icon: '🗄️' };
};

export const rdsInstanceTypes: InstanceType[] = [
  { id: 'db.t3.micro', name: 'db.t3.micro', cpu: '2 vCPUs', ram: '1 GB', price: '$0.017/hr', category: 'general' },
  { id: 'db.t3.small', name: 'db.t3.small', cpu: '2 vCPUs', ram: '2 GB', price: '$0.034/hr', category: 'general' },
  { id: 'db.t3.medium', name: 'db.t3.medium', cpu: '2 vCPUs', ram: '4 GB', price: '$0.068/hr', category: 'general' },
  { id: 'db.r5.large', name: 'db.r5.large', cpu: '2 vCPUs', ram: '16 GB', price: '$0.24/hr', category: 'memory' },
  { id: 'db.r5.xlarge', name: 'db.r5.xlarge', cpu: '4 vCPUs', ram: '32 GB', price: '$0.48/hr', category: 'memory' },
  { id: 'db.r5.2xlarge', name: 'db.r5.2xlarge', cpu: '8 vCPUs', ram: '64 GB', price: '$0.96/hr', category: 'memory' },
];

const mysqlInstanceTypes: InstanceType[] = [
  { id: 'db.t3.micro', name: 'db.t3.micro', cpu: '2 vCPUs', ram: '1 GB', price: '$0.017/hr', category: 'general' },
  { id: 'db.t3.small', name: 'db.t3.small', cpu: '2 vCPUs', ram: '2 GB', price: '$0.034/hr', category: 'general' },
  { id: 'db.t3.medium', name: 'db.t3.medium', cpu: '2 vCPUs', ram: '4 GB', price: '$0.068/hr', category: 'general' },
  { id: 'db.r5.large', name: 'db.r5.large', cpu: '2 vCPUs', ram: '16 GB', price: '$0.24/hr', category: 'memory' },
];

const sqlServerInstanceTypes: InstanceType[] = [
  { id: 'db.t3.small', name: 'db.t3.small', cpu: '2 vCPUs', ram: '2 GB', price: '$0.20/hr', category: 'general' },
  { id: 'db.t3.medium', name: 'db.t3.medium', cpu: '2 vCPUs', ram: '4 GB', price: '$0.39/hr', category: 'general' },
  { id: 'db.r5.large', name: 'db.r5.large', cpu: '2 vCPUs', ram: '16 GB', price: '$0.95/hr', category: 'memory' },
  { id: 'db.r5.xlarge', name: 'db.r5.xlarge', cpu: '4 vCPUs', ram: '32 GB', price: '$1.90/hr', category: 'memory' },
];

const managedInstanceTypes: InstanceType[] = [
  { id: 'db.r5.large', name: 'db.r5.large', cpu: '2 vCPUs', ram: '16 GB', price: '$0.29/hr', category: 'general' },
  { id: 'db.r5.xlarge', name: 'db.r5.xlarge', cpu: '4 vCPUs', ram: '32 GB', price: '$0.58/hr', category: 'memory' },
  { id: 'db.r5.2xlarge', name: 'db.r5.2xlarge', cpu: '8 vCPUs', ram: '64 GB', price: '$1.16/hr', category: 'memory' },
];

export const ec2InstanceTypes: InstanceType[] = [
  { id: 't3.micro', name: 't3.micro', cpu: '2 vCPUs', ram: '1 GB', price: '$0.0104/hr', category: 'general' },
  { id: 't3.small', name: 't3.small', cpu: '2 vCPUs', ram: '2 GB', price: '$0.0208/hr', category: 'general' },
  { id: 't3.medium', name: 't3.medium', cpu: '2 vCPUs', ram: '4 GB', price: '$0.0416/hr', category: 'general' },
  { id: 't3.large', name: 't3.large', cpu: '2 vCPUs', ram: '8 GB', price: '$0.0832/hr', category: 'general' },
  { id: 't3.xlarge', name: 't3.xlarge', cpu: '4 vCPUs', ram: '16 GB', price: '$0.1664/hr', category: 'general' },
];

/** Returns the appropriate instance types based on the engine */
export const getInstanceTypes = (engine: string): InstanceType[] => {
  switch (engine) {
    case 'mysql':
    case 'mariadb':
      return mysqlInstanceTypes;
    case 'sqlserver':
      return sqlServerInstanceTypes;
    case 'documentdb':
    case 'aurora-mysql':
    case 'aurora-postgresql':
      return managedInstanceTypes;
    case 'mongodb':
      return ec2InstanceTypes;
    default:
      return rdsInstanceTypes;
  }
};

/** @deprecated Use getInstanceTypes(engine) instead */
export const instanceTypes = rdsInstanceTypes;